import React, { useState, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { List, ListItemButton, ListItemIcon, Collapse, Box, IconButton, Tooltip } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import MoreIcon from '@mui/icons-material/MoreVertRounded';
import UpIcon from '@mui/icons-material/ArrowUpwardRounded';
import DownIcon from '@mui/icons-material/ArrowDownwardRounded';
import ClearIcon from '@mui/icons-material/DeleteForeverRounded';
import DuplicateIcon from '@mui/icons-material/FilterNoneRounded';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import WaveLayerActions from '../../WaveLayersEditor/components/WaveLayerActions';
import ImageElementThumbnail from './ImageElementThumbnail';
import ImageElementSizeListItem from './inputs/ImageElementSizeListItem';
import ImageElementPositionListItem from './inputs/ImageElementPositionListItem';
import ImageElementShadowListItem from './inputs/shadow/ImageElementShadowListItem';
import { createActions } from '../../../store';
import { mediaPickerActions } from '../../../MediaPickers/store';
import { getMediaSourceSingleUrl } from '../../../helpers';

const styles = theme => ({
    item: {
        background: theme.palette.action.hover,
    },
    collapse: {
        cursor: 'default',
        backgroundColor: theme.palette.background.paperDark
    }
});

const ImageElementListItem = (props) => {
    const {
        classes, index, imageElement, remove, moveUp, moveDown, duplicate,
        updateImageElement, openImageElementPicker
    } = props;

    // The row shows the picture itself and nothing else, so the name only exists for a screen reader
    // and for anything driving the panel from the outside.
    const elementName = `Image ${index + 1}`;

    const [open, setOpen] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);

    const toggleVisibility = useCallback(() => {
        updateImageElement(index, { visible: !imageElement.visible });
    }, [index, imageElement.visible, updateImageElement]);

    const actions = useMemo(() => [
        { label: 'Duplicate', icon: <DuplicateIcon />, onClick: duplicate },
        { label: imageElement.visible ? 'Hide' : 'Show', icon: imageElement.visible ? <VisibilityIcon /> : <VisibilityOffIcon />, onClick: toggleVisibility },
        { label: 'Move Down', icon: <DownIcon />, onClick: moveDown },
        { label: 'Move Up', icon: <UpIcon />, onClick: moveUp },
        { label: 'Remove', icon: <ClearIcon />, onClick: remove }
    ], [duplicate, moveDown, moveUp, remove, imageElement.visible, toggleVisibility]);

    const decoratedActions = useMemo(() => actions.map(action => ({
        ...action,
        onClick: action.onClick ? () => {
            setMenuAnchorEl(null);
            action.onClick();
        } : null
    })), [actions]);

    const onMoreClick = (event) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
    };

    const onThumbnailClick = (event) => {
        event.stopPropagation();
        openImageElementPicker(index);
    };

    return (
        <>
            <ListItemButton
                onClick={() => setOpen(!open)}
                aria-expanded={open}
                aria-label={`${elementName} settings`}
                sx={{ cursor: 'pointer' }}
                className={open ? classes.item : null}
            >
                <ListItemIcon onClick={onThumbnailClick}>
                    <Tooltip title='Change image' placement='top' arrow>
                        <Box>
                            <ImageElementThumbnail
                                imageUrl={getMediaSourceSingleUrl(imageElement.mediaSource)}
                                dimmed={!imageElement.visible}
                            />
                        </Box>
                    </Tooltip>
                </ListItemIcon>
                <Box sx={{ flexGrow: 1 }} />
                <Box>
                    <IconButton
                        size='small'
                        aria-haspopup='true'
                        aria-label={`${elementName} actions`}
                        onClick={onMoreClick}
                    >
                        <MoreIcon />
                    </IconButton>
                </Box>
            </ListItemButton>

            <WaveLayerActions
                actions={decoratedActions}
                anchorEl={menuAnchorEl}
                onClose={() => setMenuAnchorEl(null)}
            />

            <Collapse
                in={open}
                timeout='auto'
                unmountOnExit
                className={classes.collapse}
            >
                <List dense disablePadding>
                    <ImageElementSizeListItem index={index} />
                    <ImageElementPositionListItem index={index} />
                    <ImageElementShadowListItem index={index} />
                </List>
            </Collapse>
        </>
    );
};

const mapStateToProps = (state, ownProps) => ({
    imageElement: (state.createPreview.present.videoSettings.elements.imageElements || state.createPreview.present.videoSettings.elements.images)[ownProps.index]
});

const mapDispatchToProps = (dispatch) => {
    const updateImageElement = (index, properties) => dispatch(createActions.updateImageElement(index, properties));
    const openImageElementPicker = (index) => dispatch(mediaPickerActions.openImageElementPicker(index));

    return { updateImageElement, openImageElementPicker };
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(ImageElementListItem, styles));
